import { Box, Button, Card, CardActions, CardContent, createStyles, makeStyles } from '@material-ui/core';
import React, { ReactElement } from 'react';
import token from '../../Resources/token.svg';
import strings from '../Utils/LocalizedStrings';

interface PackageCardProps {
  /** The name of the package */
  readonly title: string;
  /** The start date from which the package begins, in a user-friendly representation */
  readonly displayableDateFrom: string;
  /** The end date from which the package ends, in a user-friendly representation */
  readonly displayableDateTo: string | null;
  /** The image that represents the package */
  readonly image: string;
  /** The total amount of tokens that are left */
  readonly tokenCount?: string;
  /** The weekly limit of tokens that the client can use */
  readonly weeklyTokenLimit?: number;
  /** The image used for displaying the status of the package */
  readonly statusImage?: ReactElement;
  /** The color of the status */
  readonly statusColor?: string;
  /** The text that describes the status */
  readonly statusText?: string;
  /** Whether the edit package button is visible or not. */
  readonly isEditPackageEnabled?: boolean;
  /** Whether the edit package button is visible or not. */
  readonly isDeletePackageEnabled?: boolean;
  /** Called when the user pressed on the Edit button. */
  readonly onEditClicked?: () => void;
  /** Called when the user pressed on the Delete button. */
  readonly onDeleteClicked?: () => void;
}

const useStyles = makeStyles((theme) => createStyles({
  card: {
    borderStyle: "solid",
    borderRadius: theme.spacing(1),
    borderWidth: "1px",
    borderColor: theme.palette.grey[400]
  },
  textContainer: {
    display: "flex",
    flexDirection: "column"
  },
  horizontalContainer: {
    marginTop: theme.spacing(1),
    display: "flex",
    flexDirection: "row"
  },
  title: {
    ...theme.typography.h5
  },
  subtitle: {
    ...theme.typography.h6
  },
  tokenImage: {
    width: theme.spacing(4),
    height: theme.spacing(4)
  },
  packageImage: {
    marginLeft: theme.spacing(2),
    width: "104px",
    height: "104px",
    objectFit: "cover"
  },
  buttonsContainer: {
    display: "flex",
    flexDirection: "row",
    flexGrow: 1,
    justifyContent: "flex-end"
  },
  deleteButton: {
    color: theme.palette.error.main
  }
}));

/**
 * A card used for display package info.
 * 
 * @param props the props that contain th data to display.
 */
const PackageCard = (props: PackageCardProps & { className?: string }) => {
  const classes = useStyles(props.image);
  return (
    <Card className={`${classes.card} ${props.className}`} elevation={0}>
      <CardContent>
        <Box className={classes.horizontalContainer}>
          <Box className={classes.textContainer}>
            <span className={classes.title}>{props.title}</span>
            <Box className={classes.horizontalContainer}>
              <span>{strings.commons.from}&nbsp;</span>
              <b>{props.displayableDateFrom}</b>
              {
                props.displayableDateTo
                  ? <span>&nbsp;{strings.commons.to}&nbsp;<b>{props.displayableDateTo}</b></span>
                  : <span />
              }
            </Box>
            {
              props.weeklyTokenLimit
                ? <Box className={classes.horizontalContainer}>
                  <b>{props.weeklyTokenLimit}</b>
                  <span>&nbsp;{strings.admin.clients.detail.tokensPerWeek}</span>
                </Box>
                : null
            }
            {
              props.tokenCount
                ? <Box className={classes.horizontalContainer}>
                  <img className={classes.tokenImage} alt={strings.alt.token} src={token} />
                  <b>{props.tokenCount}</b>
                </Box>
                : null
            }
            {
              props.statusText
                ? <Box className={classes.horizontalContainer}>
                  {props.statusImage ?? null}
                  <span>&nbsp;</span>
                  <span style={{ color: props.statusColor ?? "#000" }}>{props.statusText}</span>
                </Box>
                : null
            }
          </Box>
          <img src={props.image} alt={strings.alt.package} className={classes.packageImage} />
        </Box>
      </CardContent>
      <CardActions>
      <Box className={classes.buttonsContainer}>
        {
          props.isEditPackageEnabled
            ? <Button
              size="small"
              onClick={() => props.onEditClicked?.()}>{strings.commons.edit}</Button>
            : null
        }
        {
          props.isDeletePackageEnabled
            ? <Button
              className={classes.deleteButton}
              size="small"
              onClick={() => props.onDeleteClicked?.()}>{strings.commons.delete}</Button>
            : null
        }
        </Box>
      </CardActions>
    </Card>
  )
}

export default PackageCard;